import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import useInitPurchase from '@hooks/useInitPurchase';
import FormLayout from '@/containers/formLayout';
import ClientAddress from '@/containers/clientAddress';
import ChooseAddress from '@/containers/chooseAddress';
import ClientPayment from '@/containers/clientPayment';
import ClientPersonalData from '@/containers/clientPersonalData';
import FormBafClientFake from '@/components/fakes/formPagesFake/formBafClientFake';
import {
  OPACITY_LAYOUT_COLORS,
  CONTENT_POSITION,
  IN_SCENE_EFFECT,
  OUT_SCENE_EFFECT,
} from '@/bit/components/components.modal';
import { PERSONAL_DATA } from '@/constants/personalData';
import { SEGMENT, BUBBLE_TEXTS, DEVICE_KIND } from '@/constants/constant';
import { FORM_ID, TRANSACTION_TYPE } from '@/constants/pages/baf';
import getDeviceKindDependsOnViewPort from '@gcloud-npm/utils.common.get-device-kind-depends-on-viewport';
import { actions } from '@/reducers/form-baf-reducer';
import { INSTALLATION_ADDRESS } from '@/constants/installationAddress';
import Title, { TITLE_SIZE } from '@/components/title';
import { capitalizeWords, sendTaggingInfo } from '@/utils/common';
import { actions as modalActions } from '@/bit/reducers/reducers.modals-reducer';
import { actions as retargetingActions } from '@/reducers/retargeting-reducer';

const BafClientForm = ({ segment = SEGMENT.particulares }) => {
  const dispatch = useDispatch();
  const initialShoppingCart = useSelector(
    (state) => state.formBaf.initialShoppingCart
  );
  const shoppingCart = useSelector((state) => state.formBaf.shoppingCart);
  const loadingShoppingCart = useSelector(
    (state) => state.formBaf.loadingShoppingCart
  );
  const checkout = useSelector((state) => state.formBaf.checkout);
  const customerInfo = useSelector((state) => state.formBaf.customerInfo);
  const nextSteps = useSelector((state) => state.formBaf.nextSteps);
  const [showErrorBubble, setShowErrorBubble] = useState(false);

  const {
    unregister,
    getValues,
    handleSubmit,
    resetField,
    setValue,
    control,
    trigger,
  } = useForm({
    mode: 'onTouched',
  });

  const onSubmit = async (formData) => {
    setShowErrorBubble(false);
    const deviceKind = getDeviceKindDependsOnViewPort();
    dispatch(actions.checkout({ formData, deviceKind }));
  };
  const onError = (formErrors) => {
    if (Object.keys(formErrors).length > 1) {
      setShowErrorBubble(true);
      if ('scrollTo' in window && typeof window.scrollTo === 'function') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        });
      }
    } else if (Object.keys(formErrors).length === 1) {
      setShowErrorBubble(false);
      const scrollElement = document.getElementById(Object.keys(formErrors)[0]);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  };
  const { setSegment, setFirstZone, initPurchase } = actions;
  useInitPurchase({
    segment,
    initPurchase,
    setSegment,
    setZone: setFirstZone,
    loaded: !!shoppingCart,
  });

  useEffect(() => {
    if (
      initialShoppingCart !== null &&
      window.grecaptcha &&
      window.grecaptcha.enterprise
    ) {
      window.grecaptcha.enterprise.ready(() => {
        dispatch(
          retargetingActions.sendRetargeting({
            ...initialShoppingCart,
            campaignId: 'ret-baf-cart',
          })
        );
        const { telcoProductName, totalRecurringPrice } = {
          ...initialShoppingCart,
        };
        const { finalPrice } = {
          ...totalRecurringPrice,
        };
        sendTaggingInfo(
          {
            product: telcoProductName,
            price: finalPrice,
            funnelPurchaseMode: '',
            userType: '',
            funnelStepName: 'formulario',
            transactionType: TRANSACTION_TYPE,
            funnelFlowName: 'movistar-layer',
          },
          '',
          'movistar_layer_google_ads'
        );
      });
    }
  }, [initialShoppingCart, dispatch]);

  const handleValidateEditContactPhone = (phone) => {
    if (phone) {
      setValue(PERSONAL_DATA.MODAL_CONTACT_PHONE.ID, phone);
    }
  };
  const handleEditContactPhone = () => {
    const deviceKind = getDeviceKindDependsOnViewPort();
    const ModalLegalConditions = dynamic(() =>
      import('@/modals/changeContactPhoneModal')
    );
    dispatch(
      modalActions.addModal({
        createAnchor: false,
        type: 'MODAL',
        id: 'adviseChangeZone',
        Element2Show: ModalLegalConditions,
        elementProps: {
          validate: handleValidateEditContactPhone,
          fieldName: PERSONAL_DATA.CONTACT_PHONE,
          control,
          trigger,
          getValues,
          unregister,
        },
        modalProps: {
          className: '',
          contentPosition:
            deviceKind === DEVICE_KIND.MOBILE
              ? CONTENT_POSITION.CENTER_BOTTOM
              : CONTENT_POSITION.CENTER_CENTER,
          opacityLayoutColor: OPACITY_LAYOUT_COLORS.BLACK05,
          inSceneEffect:
            deviceKind === DEVICE_KIND.MOBILE
              ? IN_SCENE_EFFECT.FADE_IN_BOTTOM
              : IN_SCENE_EFFECT.FADE_IN,
          outSceneEffect:
            deviceKind === DEVICE_KIND.MOBILE
              ? OUT_SCENE_EFFECT.FADE_OUT_BOTTOM
              : OUT_SCENE_EFFECT.FADE_OUT,
          closeByClickingOutside: true,
          contentInsideContainer: deviceKind !== DEVICE_KIND.MOBILE,
        },
      })
    );
  };
  return (
    <>
      <Head>
        <script
          async
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_LAYER_RECAPTCHA_ID}`}
        />
      </Head>
      <FormLayout
        pageId={FORM_ID.GP}
        onSubmit={handleSubmit(onSubmit, onError)}
        control={control}
        showErrorBubble={showErrorBubble}
        textBubble={BUBBLE_TEXTS.BUBBLE_ERROR_TEXT}
        loaded={!!shoppingCart}
        shoppingCart={shoppingCart}
        loadingShoppingCart={loadingShoppingCart}
        nextStepsText={nextSteps}
        loadingButtonShoppingCart={checkout}
        formFake={<FormBafClientFake />}
      >
        <Title
          text={`Hola${
            customerInfo?.name ? ` ${capitalizeWords(customerInfo?.name)}` : ''
          }, completa tu pedido`}
          size={TITLE_SIZE.TITLE5}
        />
        {!customerInfo?.installationAddress?.addressesList && (
          <ClientAddress
            unregister={unregister}
            control={control}
            getValues={getValues}
            resetField={resetField}
            address={
              customerInfo?.installationAddress.addressSelected?.fullAddress
            }
            addressFormStructure={INSTALLATION_ADDRESS}
            addressAction={actions.setInfo}
            editable={customerInfo?.installationAddress.editable}
            setValue={setValue}
          />
        )}
        {customerInfo?.installationAddress?.addressesList && (
          <ChooseAddress
            control={control}
            addresses={customerInfo.installationAddress.addressesList}
            addressSelected={customerInfo.installationAddress.addressSelected}
            getValues={getValues}
            addressAction={actions.setInfo}
          />
        )}
        {customerInfo && customerInfo.bankAccount && (
          <ClientPayment
            control={control}
            bankAccount={customerInfo.bankAccount?.numberBankAccount}
          />
        )}
        <ClientPersonalData
          title={
            customerInfo?.contactMedia?.editable
              ? 'Datos de contacto'
              : 'Datos de contacto y pago'
          }
          contactEmail={customerInfo?.contactMedia?.contactEmail}
          contactPhone={customerInfo?.contactMedia?.contactPhone}
          payment={customerInfo?.contactMedia?.payment}
          getValues={getValues}
          edit={
            customerInfo?.contactMedia?.editable ? handleEditContactPhone : null
          }
          installationContactPhone={getValues(
            PERSONAL_DATA.MODAL_CONTACT_PHONE.ID
          )}
          control={control}
          setValue={setValue}
        />
      </FormLayout>
    </>
  );
};
export default BafClientForm;
